import React, { useEffect, useRef, useState } from "react";
import { Checkbox, Input, List, notification, Spin } from "antd";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import apiService from "../../../service/apiservice/apiService";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { getFullName } from "../../careprovider/visits/utils";
import { dateAndTime } from "../../../utils/dateutils";
import NoDataMessageComponent from "../../../components/generic/nodatamessage/NoDataMessageComponent";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { performPostRequest } from "../../../service/network/NetworkService";
import { getApiUrl } from "../../../service/network/UrlMapper";
import ErrorNotificationComponent from "../../../components/generic/error/ErrorNotificationComponent";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../utils/configurationconstants";
import { useRecoilValue } from "recoil";
import StrapiService from "../../articles/StrapiService";
import { patientUpdatePayload } from "../../generic/workforcesuggestion/utils/notificationpayload";
import { executeIntent } from "../../utils/IntentExecutor";

const { TextArea } = Input;

const ChatBox = ({ patient }) => {
  const user = useReadMemory(MemoryType.USER);
  const organizationIdentifier = useGetOrgIdentifier();
  const chatMessagesRef = useRef(null);
  const configuration = useRecoilValue(currentConfiguration);
  const [messages, setMessages] = useState([]);
  const [assignedWorkforce, setAssignedWorkforce] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [notify, setNotify] = useState(true);
  const [workforceDetails, setWorkforceDetails] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const secretKey =
    configuration[ConfigurationConstants.NIRAMAYA_ORGANIZATION_SECRETKEY];
  const strapiService = new StrapiService();

  const getRoleInfo = (currentRole) => {
    const role = {
      "Volunteer Coordinator": "Volunteer",
      Volunteer: "Volunteer Coordinator",
    };

    const rolePath = {
      "Volunteer Coordinator": "careprovider",
      Volunteer: "carecoordinator",
    };
    return {
      oppositeRole: role[currentRole],
      oppositePath: rolePath[currentRole],
    };
  };

  const handleSend = async () => {
    const recipients = assignedWorkforce.map((item) => ({
      recipient: item?.userIdentifier,
      recipientType: "workforce",
      metadata: "",
    }));
    if (inputValue.trim() && recipients?.length) {
      const message = {
        type: "text",
        message: `Patient (#${patient?.medicalRecordNumber}) details updated.`,
        chatMessage: inputValue,
        actions: [
          {
            label: "View Profile",
            actionType: "redirect",
            metadata: {
              url: `patient-profile?identifier=${patient?.userIdentifier}`,
            },
          },
        ],
      };
      const payload = {
        sender: user?.userIdentifier,
        senderType: "workforce",
        message: JSON.stringify(message),
        metadata: "",
        organizationIdentifier,
        recipients,
        keywords: [
          notify && {
            keyword: "notification",
            metadata: "",
          },
          {
            keyword: `patient_${patient?.userIdentifier}`,
            metadata: "",
          },
        ].filter(Boolean),
      };
      const response = await apiService.post(
        "niramayafoundation/api/v1/communication/message",
        payload
      );

      if (response?.id) {
        setRefresh((prev) => !prev);
        setInputValue("");
        await handleSendMail();
      } else {
        notification.error({
          message: "Failed to add note",
          placement: "bottomRight",
        });
      }
      setButtonLoading(false);
    }
  };

  const handleSendMail = async () => {
    const roleInfo = getRoleInfo(user?.jobTitle);
    const receivers = assignedWorkforce.map((item) => ({
      username: item?.email,
      email: item?.email,
      number: item?.telephone,
    }));

    const emailBody = templateData?.email_body
      .replace("{role}", roleInfo?.oppositeRole)
      .replace("{mrn}", patient?.medicalRecordNumber)
      .replace("{rolePath}", roleInfo?.oppositePath)
      .replace("{identifier}", patient?.userIdentifier);
    const clickAction = templateData?.clickAction
      .replace("{rolePath}", roleInfo?.oppositePath)
      .replace("{identifier}", patient?.userIdentifier);
    const smsMessage = templateData?.sms_message
      .replace("{rolePath}", roleInfo?.oppositePath)
      .replace("{identifier}", patient?.userIdentifier);
    const fcmBody = templateData?.fcm_notification_body.replace(
      "{mrn}",
      patient?.medicalRecordNumber
    );

    const payload = patientUpdatePayload(
      receivers,
      templateData?.email_subject,
      emailBody,
      fcmBody,
      templateData?.fcm_title,
      smsMessage,
      user?.userIdentifier,
      organizationIdentifier,
      templateData?.fcm_appId,
      clickAction
    );
    await executeIntent(IntentType.SEND_NOTIFICATION, payload, {});
  };

  useEffect(() => {
    const getTemplate = async () => {
      const result = await strapiService.getTemplate(
        "patient_updates",
        organizationIdentifier
      );
      const mailTemplate = result?.data[0]?.attributes;
      setTemplateData(mailTemplate);
    };

    getTemplate();
  }, [organizationIdentifier]);

  useEffect(() => {
    const getChat = async () => {
      setLoading(true);
      const endpoint = "niramayafoundation/api/v1/communication/message";
      const params = {
        organizationIdentifier,
        keywords: `patient_${patient?.userIdentifier}`,
      };
      const response = await apiService.get(endpoint, params);
      if (Array.isArray(response)) {
        setMessages(response.reverse());
      }
      setLoading(false);
    };

    if (patient?.userIdentifier) {
      getChat();
    }
  }, [
    organizationIdentifier,
    patient?.userIdentifier,
    refresh,
    user?.userIdentifier,
  ]);

  // get assigned workforce
  useEffect(() => {
    const getAssignedWorkforce = async () => {
      const roleInfo = getRoleInfo(user?.jobTitle);
      const endpoint =
        "niramayafoundation/niramayavisits/api/v1/careplans/assigned_workforce";
      const params = {
        patientIdentifier: patient?.userIdentifier,
        jobTitles: roleInfo.oppositeRole,
      };
      const response = await apiService.get(endpoint, params);
      if (Array.isArray(response)) {
        setAssignedWorkforce(response);
      }
    };

    if (patient?.userIdentifier) {
      getAssignedWorkforce();
    }
  }, [patient?.userIdentifier, user?.jobTitle]);

  // get workforce details
  useEffect(() => {
    const getWorkforceDetails = async () => {
      const workforceItdentifiersList = messages
        .map((item) => item?.sender)
        .filter(Boolean);

      const payload = {
        intent: IntentType.GET_WORKFORCE_DETAILS_MAP_BY_USER_IDENTIFIER,
        payload: {
          userIdentifiers: workforceItdentifiersList,
          secretKey: secretKey?.key,
        },
        context: {
          organizationIdentifier,
          userIdentifier: user?.userIdentifier,
        },
      };
      try {
        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_WORKFORCE_DETAILS_MAP_BY_USER_IDENTIFIER],
          JSON.stringify(payload)
        );
        const resultJson = await result.json();

        setWorkforceDetails(resultJson);
      } catch (error) {
        ErrorNotificationComponent(error);
      }
    };

    if (messages?.length) {
      getWorkforceDetails();
    }
  }, [messages, organizationIdentifier, secretKey?.key, user?.userIdentifier]);

  useEffect(() => {
    if (workforceDetails) {
      const container = chatMessagesRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [messages, workforceDetails]);

  return (
    <>
      <div className="chat-container">
        {loading ? (
          <Spin size="medium" className="spinnerCenter" />
        ) : (
          <>
            <div className="chat-messages" ref={chatMessagesRef}>
              {messages?.length ? (
                <List
                  dataSource={messages}
                  renderItem={(item) => {
                    const message = JSON.parse(item?.message || "{}");
                    const workforce = workforceDetails?.[item?.sender];

                    return (
                      <List.Item
                        className={
                          item?.sender === user?.userIdentifier
                            ? "user-message"
                            : "other-message"
                        }
                      >
                        <div className="message-bubble">
                          <div className="message-meta">
                            <span className="message-sender">
                              {getFullName(
                                workforce?.givenName,
                                workforce?.familyName
                              )}
                            </span>
                            {workforce?.jobTitle && (
                              <span className="message-role">
                                ({workforce?.jobTitle})
                              </span>
                            )}
                          </div>
                          <div className="message-text">
                            {message?.chatMessage}
                          </div>
                          {item?.createdAt && (
                            <div className="message-time">
                              {dateAndTime(item?.createdAt)}
                            </div>
                          )}
                        </div>
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <NoDataMessageComponent text="No data found!" />
              )}
            </div>
            <div className="chat-input">
              <Checkbox
                onChange={(e) => setNotify(e.target.checked)}
                checked={notify}
              >
                Notify
              </Checkbox>
              <TextArea
                name="inputValue"
                rows={2}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type Update Here"
              />
              <PrimaryButton
                buttonText="Add"
                handleOnClick={handleSend}
                buttonDisable={!inputValue.trim()}
                loading={buttonLoading}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ChatBox;
